import CustomTable from "@Common/CustomTable";
import { useMemo, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CardsInfo from "./cardsInfo";
import { ReservationManagementWrapper } from "./styles";
import ReservationMangmentHeader from "./reservationMangmentHeader";
import { useReservations } from "@services";
import AirbnbIcon from "@assets/svg/airbnb.svg?react";
import GathernIcon from "@assets/svg/GathernIcon2.svg?react";
import BookingIcon from "@assets/svg/BookingIcon.svg?react";
import ChannelAgoda from "@assets/svg/ChannelAgoda.svg?react";
import MsoolIcon from "@assets/svg/msool-icon.svg?react";
import { CustomNoRowsOverlay } from "./CustomNoRowsOverlay";
import { useRecoilValue } from "recoil";
import { isManualReservationOpen } from "@store/index";
import ManualReservation from "../manualReservation_v2";
import { ReservationFiltersState } from "./ReservationFilters";
import { debounce } from "lodash";
import i18n from "src/i18n";

export default function ReservationManagement() {
  const channals = {
    1: <AirbnbIcon />,
    2: <GathernIcon />,
    3: <BookingIcon />,
    4: <ChannelAgoda />,
    5: "Other",
    6: <MsoolIcon />,
  };
  const channalsTitle = {
    1: "Airbnb",
    2: "Gathern",
    3: "Booking",
    4: "ChannelA",
    5: "Other",
    6: "Msool",
  };
  const isManualReservationOpenValue = useRecoilValue(isManualReservationOpen);
  const [perPage, onPerpageChange] = useState("20");
  const [activePage, setActivePage] = useState(1);
  const [enumOptions, setEnumOptions] = useState<string[]>([
    "TeamMember",
    "ServiceProvider",
  ]);
  // Main filters state
  const [filters, setFilters] = useState<ReservationFiltersState>({
    dateRange: null,
    selectedChannels: [], // Start with no channels selected
    selectedNames: [],
    priceRange: null, // No default price range - only send when user filters
    enum: enumOptions,
  });

  // Debounced filters state for API calls
  const [debouncedFilters, setDebouncedFilters] =
    useState<ReservationFiltersState>(filters);

  // Search term for name filter
  const [nameSearchTerm, setNameSearchTerm] = useState("");

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { ReservationsQuery } = useReservations();

  // Create debounced function to update debounced filters
  const debouncedSetFilters = useCallback(
    debounce((newFilters: ReservationFiltersState) => {
      setDebouncedFilters(newFilters);
    }, 800), // Increased to 800ms delay to match NameFilter
    []
  );

  // Update debounced filters when main filters change
  useEffect(() => {
    debouncedSetFilters(filters);
  }, [filters, debouncedSetFilters]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
    };
  }, [debouncedSetFilters]);

  // Convert filter state to API parameters using debounced filters
  const apiParams = useMemo(() => {
    const params: any = {
      pageNumber: activePage.toString(),
      pageSize: perPage,
    };
    // Add date range if available
    if (
      debouncedFilters.dateRange &&
      debouncedFilters.dateRange[0] &&
      debouncedFilters.dateRange[1]
    ) {
      params.fromDate = debouncedFilters.dateRange[0].format("YYYY-MM-DD");
      params.toDate = debouncedFilters.dateRange[1].format("YYYY-MM-DD");
    }

    // Add price range only if user has explicitly set it (not null)
    if (
      debouncedFilters.priceRange !== null &&
      debouncedFilters.priceRange &&
      debouncedFilters.priceRange.length === 2
    ) {
      params.minPrice = debouncedFilters.priceRange[0];
      params.maxPrice = debouncedFilters.priceRange[1];
    }

    // Add selected channels if available
    if (
      debouncedFilters.selectedChannels &&
      debouncedFilters.selectedChannels.length > 0
    ) {
      params.channels = debouncedFilters.selectedChannels;
    }

    // Add selected names if available
    if (
      debouncedFilters.selectedNames &&
      debouncedFilters.selectedNames.length > 0
    ) {
      params.selectedNames = debouncedFilters.selectedNames;
    }

    // Add name search term if available
    if (nameSearchTerm && nameSearchTerm.trim() !== "") {
      params.search = nameSearchTerm.trim();
    }

    if (debouncedFilters.enum && debouncedFilters.enum.length > 0) {
      params.enum = debouncedFilters.enum;
    }

    return params;
  }, [activePage, perPage, debouncedFilters, nameSearchTerm, enumOptions]);

  const { data: Reservations, isLoading } = ReservationsQuery(apiParams);

  const handleFiltersChange = (newFilters: ReservationFiltersState) => {
    setFilters(newFilters);
    // Reset to first page when filters change
    setActivePage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      dateRange: null,
      selectedChannels: [], // Don't set default channel when clearing
      selectedNames: [],
      priceRange: null, // Clear price range - don't send to API
      enum: [],
    });
    setNameSearchTerm(""); // Clear name search term to empty string
    setActivePage(1);
  };

  // Handle name search term change
  const handleNameSearchChange = (searchTerm: string) => {
    setNameSearchTerm(searchTerm);
    // Reset to first page when search term changes
    setActivePage(1);
  };

  const columns = [
    {
      field: "reservationId",
      headerName: t("Reservation.BookingNum"),
      flex: 1,
      renderCell: (params: any) => (
        <p
          className="columnsContent"
          style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}
        >
          {params.row.reservationId}
        </p>
      ),
    },
    {
      field: "checkIn",
      headerName: t("Reservation.inDate"),
      flex: 1,
      renderCell: (params: any) => (
        <p
          className="columnsContent"
          style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}
        >
          {params.row.checkIn}
        </p>
      ),
    },
    {
      field: "checkOut",
      headerName: t("Reservation.outDate"),
      flex: 1,
      renderCell: (params: any) => (
        <p
          className="columnsContent"
          style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}
        >
          {params.row.checkOut}
        </p>
      ),
    },
    {
      field: "propertyName",
      headerName: t("Reservation.PropertyName"),
      flex: 1,
      renderCell: (params: any) => (
        <p
          className="columnsContent"
          style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}
        >
          {params.row.propertyName}
        </p>
      ),
    },
    {
      field: "channelName",
      headerName: t("Reservation.Platform"),
      flex: 1,
      renderCell: (params: any) => (
        <p
          className="columnsContent"
          style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}
        >
          {channals[params.row.platFormSRC as 1 | 2 | 3 | 4 | 5 | 6]}{" "}
          {channalsTitle[params.row.platFormSRC as 1 | 2 | 3 | 4 | 5 | 6]}
        </p>
      ),
    },
    {
      field: "bookingStatus",
      headerName: t("Reservation.BookingStatus"),
      flex: 1,
      renderCell: (params: any) => (
        <div className="containerStatus">
          <p
            className={`"columnsContent" ${
              params.row.bookingStatus === "مقبول"
                ? "accept"
                : params.row.bookingStatus === "مرفوض"
                  ? "reject"
                  : params.row.bookingStatus === "معلق"
                    ? "pendding"
                    : params.row.bookingStatus === "لم_يحضر_الضيف"
                      ? "reject"
                      : "known"
            }`}
            style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}
          >
            <span></span>
            {params.row.bookingStatus == "لم_يحضر_الضيف"
              ? "لم يحضر"
              : params.row.bookingStatus}
          </p>
        </div>
      ),
    },
    {
      field: "guestName",
      headerName: t("Reservation.GuestName"),
      flex: 1,
      renderCell: (params: any) => (
        <p
          className="columnsContent"
          style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}
        >
          {params.row.guestName}
        </p>
      ),
    },
    {
      field: "totalPrice",
      headerName: t("Reservation.TotalPrice"),
      flex: 1,
      renderCell: (params: any) => (
        <p
          className="columnsContent"
          style={{ textAlign: `${language === "ar" ? "right" : "left"}` }}
        >
          <span>{params.row.totalPrice}</span> <span>ريال سعودي</span>
        </p>
      ),
    },
  ];

  const rows = useMemo(() => {
    const data = Reservations?.data?.result?.reservationDTOs?.map((item) => {
      return {
        id: item.id,
        reservationId: item.reservationId,
        checkIn: item.checkIn,
        checkOut: item.checkOut,
        propertyName: item.propertyName,
        channelName: item.channelName,
        bookingStatus: item.bookingStatus,
        guestName: item.guestName,
        totalPrice: item.totalPrice,
        platFormSRC: item.platFormSRC,
      };
    });
    return data;
  }, [Reservations]);
  return (
    <ReservationManagementWrapper>
      {isManualReservationOpenValue && <ManualReservation />}
      <ReservationMangmentHeader />
      <CardsInfo />

      <div className="containerTable">
        <CustomTable
          loading={isLoading}
          columns={columns}
          rows={rows ?? []}
          HeaderTheTable={t("RESERVATIONLIST")}
          totalPages={
            Reservations?.data?.totalRecords
              ? Math.ceil(Reservations?.data?.totalRecords / Number(perPage))
              : 1
          }
          totalRecords={Reservations?.data?.totalRecords}
          setActivePage={setActivePage}
          activePage={activePage}
          onPerpageChange={onPerpageChange}
          perPage={perPage}
          CustomNoRowsOverlay={CustomNoRowsOverlay}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          reservations={rows ?? []} // Pass the reservation data
          enumOptions={enumOptions}
          onNameSearchChange={handleNameSearchChange}
          listPerPagesNumber={[
            {
              label: "20",
              value: "20",
            },
            {
              label: "50",
              value: "50",
            },
            {
              label: "100",
              value: "100",
            },
          ]}
          DateFilterPlaceholder={
            t("Reservation.FROM_AND_TO", {
              //FROM AND TO
              returnObjects: true,
            }) as [string, string]
          }
          ChannelFilterLabel={t("Reservation.CHANNEL")} //CHANNEL
          PriceRangeFilterLabel={t("Reservation.PRICE")} // TOTAL PRICE
          nameFilterPlaceholder={t("Reservation.GUEST_NAME")} //  اسم الضيف
        />
      </div>
    </ReservationManagementWrapper>
  );
}
